import assert from "assert";
import GenUtils from "../../common/GenUtils";
import MoneroError from "../../common/MoneroError";
import MoneroOutput from "../../daemon/model/MoneroOutput";
import MoneroTxWallet from "./MoneroTxWallet";
/**
 * Models a Monero output with wallet extensions.
 */
export default class MoneroOutputWallet extends MoneroOutput {
    /**
     * Construct the model.
     *
     * @param {MoneroOutputWallet} [output] is existing state to initialize from (optional)
     */
    constructor(output) {
        super(output);
    }
    getTx() {
        return super.getTx();
    }
    setTx(tx) {
        if (tx !== undefined && !(tx instanceof MoneroTxWallet))
            throw new MoneroError("Wallet output's transaction must be of type MoneroTxWallet");
        super.setTx(tx);
        return this;
    }
    getAccountIndex() {
        return this.accountIndex;
    }
    setAccountIndex(accountIndex) {
        this.accountIndex = accountIndex;
        return this;
    }
    getSubaddressIndex() {
        return this.subaddressIndex;
    }
    setSubaddressIndex(subaddressIndex) {
        this.subaddressIndex = subaddressIndex;
        return this;
    }
    getIsSpent() {
        return this.isSpent;
    }
    setIsSpent(isSpent) {
        this.isSpent = isSpent;
        return this;
    }
    /**
     * Indicates if this output has been deemed 'malicious' and will therefore
     * not be spent by the wallet.
     *
     * @return Boolean is whether or not this output is frozen
     */
    getIsFrozen() {
        return this.isFrozen;
    }
    setIsFrozen(isFrozen) {
        this.isFrozen = isFrozen;
        return this;
    }
    getIsLocked() {
        if (this.getTx() === undefined)
            return undefined;
        return this.getTx().getIsLocked();
    }
    copy() {
        return new MoneroOutputWallet(this.toJson());
    }
    toJson() {
        let json = Object.assign({}, this, super.toJson());
        delete json.tx;
        return json;
    }
    /**
     * Updates this output by merging the latest information from the given
     * output.
     *
     * Merging can modify or build references to the output given so it
     * should not be re-used or it should be copied before calling this method.
     *
     * @param output is the output to merge into this one
     */
    merge(output) {
        assert(output instanceof MoneroOutputWallet);
        if (this === output)
            return;
        super.merge(output);
        this.setAccountIndex(GenUtils.reconcile(this.getAccountIndex(), output.getAccountIndex()));
        this.setSubaddressIndex(GenUtils.reconcile(this.getSubaddressIndex(), output.getSubaddressIndex()));
        this.setIsSpent(GenUtils.reconcile(this.getIsSpent(), output.getIsSpent(), { resolveTrue: true })); // output can become spent
        this.setIsFrozen(GenUtils.reconcile(this.getIsFrozen(), output.getIsFrozen()));
        return this;
    }
    toString(indent = 0) {
        let str = super.toString(indent) + "\n";
        str += GenUtils.kvLine("Account index", this.getAccountIndex(), indent);
        str += GenUtils.kvLine("Subaddress index", this.getSubaddressIndex(), indent);
        str += GenUtils.kvLine("Is spent", this.getIsSpent(), indent);
        str += GenUtils.kvLine("Is frozen", this.getIsFrozen(), indent);
        return str.slice(0, str.length - 1); // strip last newline
    }
    // -------------------- OVERRIDE COVARIANT RETURN TYPES ---------------------
    setKeyImage(keyImage) {
        super.setKeyImage(keyImage);
        return this;
    }
    setAmount(amount) {
        super.setAmount(amount);
        return this;
    }
    setIndex(index) {
        super.setIndex(index);
        return this;
    }
    setRingOutputIndices(ringOutputIndices) {
        super.setRingOutputIndices(ringOutputIndices);
        return this;
    }
    setStealthPublicKey(stealthPublicKey) {
        super.setStealthPublicKey(stealthPublicKey);
        return this;
    }
}
